import React, {useRef, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {SageSyncAnalysisPage_viewer} from './__generated__/SageSyncAnalysisPage_viewer.graphql';
import {ComboBox, FlexColumn, FlexRow, Label, Text} from '@forecast-it/design-system';
import Styled from 'styled-components';
import MissionControlHeader from './MissionControlHeader';
import SageSyncAnalysisTable, {sageSyncAnalysisTableQuery} from './SageSyncAnalysisTable';
import ForecastQueryRenderer from '../../../ForecastQueryRenderer';
import {idFromGlobalId} from '../../../forecast-app/shared/util/GlobalIdUtil';

interface ComboItem {
	id?: number;
	name?: string;
}

interface SageSyncAnalysisPageProps {
	viewer: SageSyncAnalysisPage_viewer;
}

const PageWrapper = Styled.div`
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const InfoHeader = Styled.div`
  padding-left: 64px;
  padding-top: 16px;
`;

export const formatDate = (date: Date): string => {
	return date.toLocaleDateString('en-GB', {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	});
};

function SageSyncAnalysisPage({viewer}: SageSyncAnalysisPageProps): React.JSX.Element {
	const companySyncAnalysisHistory = viewer.missionControlData?.companySyncAnalysisHistory?.map(t => ({
		id: t?.id ? idFromGlobalId(t.id) : undefined,
		name: t?.syncDate ? formatDate(new Date(t.syncDate)) : undefined,
	}));

	const idString = viewer.missionControlData?.companySyncAnalysis?.id
		? viewer.missionControlData?.companySyncAnalysis.id
		: null;

	const companyName = viewer.missionControlData?.companySyncAnalysis?.companyName;

	const companyId = viewer.missionControlData?.companySyncAnalysis?.companyId;
	const [analysisId, setAnalysisId] = useState(idString ? idFromGlobalId(idString) : undefined);

	const scrollRef = useRef(null);

	return (
		<PageWrapper ref={scrollRef}>
			<MissionControlHeader viewer={viewer} />
			<InfoHeader>
				<FlexColumn>
					<FlexRow>
						<Label>Company ID</Label>
						<Text>{companyId}</Text>
					</FlexRow>
					<FlexRow>
						<Label>Company Name</Label>
						<Text>{companyName}</Text>
					</FlexRow>
					<FlexRow>
						<ComboBox value={analysisId} onChange={setAnalysisId} items={companySyncAnalysisHistory}>
							{(item: ComboItem) => (
								<ComboBox.Item textValue={item.name}>
									<Text>{item.name}</Text>
								</ComboBox.Item>
							)}
						</ComboBox>
					</FlexRow>
				</FlexColumn>
			</InfoHeader>

			<ForecastQueryRenderer
				isMc={true}
				key="query-render-sageSyncAnalysisTable"
				query={sageSyncAnalysisTableQuery}
				variables={{
					companyId: companyId,
					analysisId: analysisId,
				}}
				render={relayProps => {
					return <SageSyncAnalysisTable {...relayProps} scrollRef={scrollRef} />;
				}}
			/>
		</PageWrapper>
	);
}

const sageSyncAnalysisQuery = graphql`
	query SageSyncAnalysisPage_Query($companyId: Int, $analysisId: Int, $forceRefresh: Boolean) {
		viewer {
			actualPersonId
			component(name: "sage_sync_analysis")
			...SageSyncAnalysisPage_viewer
				@arguments(companyId: $companyId, analysisId: $analysisId, forceRefresh: $forceRefresh)
		}
	}
`;

export {sageSyncAnalysisQuery};

export default createFragmentContainer(SageSyncAnalysisPage, {
	viewer: graphql`
		fragment SageSyncAnalysisPage_viewer on Viewer
		@argumentDefinitions(companyId: {type: "Int"}, analysisId: {type: "Int"}, forceRefresh: {type: "Boolean"}) {
			id
			...MissionControlHeader_viewer
			missionControlData {
				companySyncAnalysis(companyId: $companyId, analysisId: $analysisId, forceRefresh: $forceRefresh) {
					id
					companyId
					companyName
				}
				companySyncAnalysisHistory(companyId: $companyId) {
					id
					forecastCompanyId
					syncDate
				}
			}
		}
	`,
});
